import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  ShoppingCart,
  Search,
  CreditCard,
  Gift,
  Banknote,
  Send,
  X,
  Receipt,
  Package2,
  Filter,
  ChevronDown,
  AlertCircle,
  History,
} from "lucide-react";
import { useCategories } from "../../context/CategoriesContext";
import CartContent from "../../components/NonCustomer/CartContent";
import BaseApi from "../../services/base-api";
import { Product, ProductVariation } from "../../types/Product";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../context/ToastContext";
import { useTranslation } from "react-i18next";
import Price from "../../components/Currency/Price";
import { PointOfSale } from "@mui/icons-material";
import LoadingComponent from "../../components/LoadingComponent";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import { useAuth } from "../../context/AuthContext";
import { Tooltip } from "@mui/material";
import Pagination from "../../components/Pagination";
import { useCurrency } from "../../components/Currency/CurrencyContext";
import { useDebounce } from "../../hooks/useDebounce";

interface CartItem {
  id: string;
  productId: string;
  name: string;
  sku: string;
  price: number;
  quantity: number;
  total: number;
  stock: number;
  variationId?: string;
  variationAttributes?: {
    [key: string]: string;
  };
}

interface CustomerInfo {
  name: string;
  phone: string;
  city: string;
  zone: string;
  street: string;
  buildingNumber: string;
  unitNumber: string;
}

const emptyCustomerInfo: CustomerInfo = {
  name: "",
  phone: "",
  city: "",
  zone: "",
  street: "",
  buildingNumber: "",
  unitNumber: "",
};

const POSPage = () => {
  const navigate = useNavigate();
  const { categories } = useCategories();
  const { t, i18n } = useTranslation();
  const { success, err } = useToast();
  const { currency } = useCurrency();
  const { permissions } = useAuth();
  const canViewHistory = hasPermission(
    permissions,
    PERMISSIONS.VIEW_POS_HISTORY,
  );

  const [products, setProducts] = useState<Product[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const [filtered, setFiltered] = useState<Product[]>([]);
  const [customerInfo, setCustomerInfo] =
    useState<CustomerInfo>(emptyCustomerInfo);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showCartMobile, setShowCartMobile] = useState(false);

  const [showVariationModal, setShowVariationModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [variationModalLoading, setVariationModalLoading] = useState(false);
  const [variationModalError, setVariationModalError] = useState<string | null>(
    null,
  );
  const [selectedVariationAttributes, setSelectedVariationAttributes] =
    useState<Record<string, string>>({});
  const [paymentMethod, setPaymentMethod] = useState<
    "cash" | "card" | "credit" | "send_link" | "maagoud_gift"
  >("cash");
  const [amountReceived, setAmountReceived] = useState("");
  // Internal staff-only note; never shown to the customer (unlike `notes`)
  const [remarks, setRemarks] = useState("");
  const [tax, setTax] = useState(0);
  const [taxType, setTaxType] = useState<"percentage">("percentage");
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState<"amount" | "percentage">(
    "amount",
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastReceipt, setLastReceipt] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [apiTotalItems, setApiTotalItems] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  const debouncedSearch = useDebounce(searchQuery.trim());
  const latestRequestRef = useRef(0);

  const fetchProducts = useCallback(async () => {
    const requestId = ++latestRequestRef.current;
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
      });

      // Add search parameter if search query exists
      if (debouncedSearch) {
        params.append("search", debouncedSearch);
      }

      // Add category parameter if selected
      if (selectedCategoryId) {
        params.append("categoryId", selectedCategoryId);
      }

      const response = await BaseApi.get(`/products?${params.toString()}`);
      if (requestId !== latestRequestRef.current) return;

      if (response.success && response.data) {
        setProducts(response.data);
        // Set total items from API pagination info
        setTotalProducts(
          response.pagination?.totalItems || response.data.length,
        );
        setFiltered(response.data); // Set filtered to API response directly
        setApiTotalItems(
          response.pagination?.totalItems || response.data.length,
        );
      } else {
        throw new Error("Failed to fetch products");
      }
    } catch (err) {
      if (requestId !== latestRequestRef.current) return;
      setError(err instanceof Error ? err.message : "Failed to load products");
      console.error("Error fetching products:", err);
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false);
        setInitialLoading(false);
      }
    }
  }, [currentPage, itemsPerPage, debouncedSearch, selectedCategoryId]);

  // Fetch products from API
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Reset to first page when the debounced search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const closeVariationModal = () => {
    setShowVariationModal(false);
    setSelectedProduct(null);
    setVariationModalError(null);
    setVariationModalLoading(false);
    setSelectedVariationAttributes({});
  };

  const handleProductClick = async (product: Product) => {
    if (product.hasVariations && product?.variationsCount > 0) {
      setShowVariationModal(true);
      setSelectedProduct(product);
      setVariationModalError(null);
      setSelectedVariationAttributes({});
      setVariationModalLoading(true);

      try {
        const res = await BaseApi.get(`/products/${product.id}`);
        const detail = res?.data?.product || res?.data;

        if (detail) {
          setSelectedProduct(detail);
        } else {
          setVariationModalError(t("Failed to load product options"));
        }
      } catch (error: any) {
        console.error("Failed to load product details:", error);
        setVariationModalError(
          error?.message || t("Failed to load product options"),
        );
      } finally {
        setVariationModalLoading(false);
      }
      return;
    }

    addToCart(product);
  };

  const addToCart = (product: Product, variation?: ProductVariation) => {
    const itemId = variation ? `${product.id}-${variation.id}` : product.id;
    const itemSku = variation ? variation.sku : product.sku;
    const itemPrice = variation ? variation.price : product.price;
    const itemStock = variation ? variation.stock : product.stock;

    const existingItem = cart?.find((item) => item.id === itemId);

    if (existingItem) {
      if (existingItem.quantity < itemStock) {
        setCart(
          cart.map((item) =>
            item.id === itemId
              ? {
                  ...item,
                  quantity: item.quantity + 1,
                  total: (item.quantity + 1) * item.price,
                }
              : item,
          ),
        );
      }
    } else {
      const cartItem: CartItem = {
        id: itemId,
        productId: product.id,
        name:
          (i18n.language === "ar" ? product.name?.ar : product.name?.en) || "",
        sku: itemSku,
        price: Number(itemPrice),
        quantity: 1,
        total: Number(itemPrice),
        stock: itemStock,
        variationId: variation?.id,
        variationAttributes: variation?.attributes,
      };
      setCart([...cart, cartItem]);
    }
    setShowCartMobile(true);
    setShowVariationModal(false);
  };

  const allowedVariationTypes = useMemo(
    () =>
      (selectedProduct?.variationTypes || [])
        .map((type) => type?.toLowerCase?.())
        .filter(Boolean),
    [selectedProduct?.variationTypes],
  );

  const variationOptions = useMemo(() => {
    const configuredTypes = allowedVariationTypes.filter(
      (type: string | undefined): type is string =>
        typeof type === "string" && type.length > 0,
    );
    const fallbackTypes = Array.from(
      new Set(
        (selectedProduct?.variations || []).flatMap((variation) =>
          Object.entries(variation.attributes || {})
            .filter(([, value]) => Boolean(value))
            .map(([key]) => key),
        ),
      ),
    );
    const variationTypesToUse =
      configuredTypes.length > 0 ? configuredTypes : fallbackTypes;
    const optionsMap: Record<string, Set<string>> = {};
    variationTypesToUse.forEach((type: string) => {
      optionsMap[type] = new Set();
    });

    (selectedProduct?.variations || []).forEach((variation) => {
      variationTypesToUse.forEach((type: string) => {
        const value = variation.attributes?.[type];
        if (value) {
          optionsMap[type].add(value);
        }
      });
    });

    return Object.fromEntries(
      Object.entries(optionsMap)
        .map(([key, set]) => [key, Array.from(set)] as const)
        .filter(([, values]) => values.length > 0),
    ) as Record<string, string[]>;
  }, [allowedVariationTypes, selectedProduct?.variations]);

  const variationKeys = Object.keys(variationOptions);
  const requiresVariationSelection =
    (selectedProduct?.variations || []).length > 0 && variationKeys.length > 0;

  const selectedVariation = useMemo(() => {
    if (!requiresVariationSelection) {
      return (selectedProduct?.variations || [])[0] || null;
    }

    if (!variationKeys.every((key) => selectedVariationAttributes[key])) {
      return null;
    }

    return (selectedProduct?.variations || []).find((variation) =>
      variationKeys.every(
        (key) =>
          variation.attributes?.[key] === selectedVariationAttributes[key],
      ),
    );
  }, [
    requiresVariationSelection,
    selectedProduct?.variations,
    selectedVariationAttributes,
    variationKeys,
  ]);

  const isVariationModalOutOfStock = selectedVariation
    ? selectedVariation.stock <= 0
    : (selectedProduct?.stock ?? 0) <= 0;

  const handleVariationAttributeClick = (type: string, value: string) => {
    setSelectedVariationAttributes((prev) => ({ ...prev, [type]: value }));
  };

  const handleAddFromVariationModal = () => {
    if (!selectedProduct) return;

    if (variationModalLoading || variationModalError) return;

    if (requiresVariationSelection && !selectedVariation) {
      err(t("Please select options"));
      return;
    }

    if (isVariationModalOutOfStock) {
      err(t("Out of Stock"));
      return;
    }

    addToCart(selectedProduct, selectedVariation || undefined);
  };

  const updateQuantity = (itemId: string, newQuantity: number) => {
    const cartItem = cart?.find((item) => item.id === itemId);

    if (newQuantity <= 0) {
      removeFromCart(itemId);
      return;
    }

    if (cartItem && newQuantity <= cartItem.stock) {
      setCart(
        cart.map((item) =>
          item.id === itemId
            ? {
                ...item,
                quantity: newQuantity,
                total: newQuantity * item.price,
              }
            : item,
        ),
      );
    }
  };

  const removeFromCart = (itemId: string) => {
    setCart(cart.filter((item) => item.id !== itemId));
  };

  const clearCart = () => {
    setCart([]);
    setCustomerInfo({ ...emptyCustomerInfo });
    setTax(0);
    setTaxType("percentage");
    setDiscount(0);
    setDiscountType("amount");
  };

  const getSubtotal = () => {
    return cart.reduce((sum, item) => sum + item.total, 0);
  };

  const getTax = () => {
    if (taxType === "percentage") {
      return (getSubtotal() * tax) / 100;
    }
    return tax;
  };

  const getDiscount = () => {
    if (discountType === "percentage") {
      return (getSubtotal() * discount) / 100;
    }
    return discount;
  };

  const getTotal = () => {
    return Math.max(getSubtotal() + getTax() - getDiscount(), 0);
  };

  const getChange = () => {
    const received = parseFloat(amountReceived) || 0;
    return received - getTotal();
  };

  const handleCheckout = () => {
    if (cart?.length === 0) return;
    setShowPaymentModal(true);
    setShowCartMobile(false);
  };

  const handleCreatePOSOrder = async (): Promise<boolean> => {
    try {
      const customerAddress = {
        city: customerInfo.city.trim(),
        zone: customerInfo.zone.trim(),
        street: customerInfo.street.trim(),
        buildingNumber: customerInfo.buildingNumber.trim(),
        unitNumber: customerInfo.unitNumber.trim(),
      };
      const hasCustomerAddress = Object.values(customerAddress).some(Boolean);

      const response = await BaseApi.post("/pos/sales", {
        items: cart.map((item) => ({
          productId: item.productId,
          variationId: item.variationId || null,
          quantity: item.quantity,
          unitPrice: item.price,
        })),
        paymentMethod,
        // tax: getTax(),
        taxRate: tax,
        discount,
        discountType,
        customerName: customerInfo.name,
        customerPhone: customerInfo.phone,
        customerAddress: hasCustomerAddress ? customerAddress : undefined,
        notes: "",
        remarks: remarks.trim() || undefined,
      });

      // console.log("Create POS Order response ----> ", response);

      if (response.success && response.data) {
        // console.log("Invoice created successfully:", response.data);
        return true;
      } else {
        throw new Error("Failed to create Invoice");
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create Invoice";
      setError(message);
      err(message);
      console.error("Error creating invoice:", error);
      return false;
    }
  };

  const processPayment = async () => {
    if (paymentMethod === "cash") {
      const received = parseFloat(amountReceived) || 0;
      if (received < getTotal()) {
        err(t("Insufficient amount received!"));
        return;
      }
    }

    if (paymentMethod === "send_link" && !customerInfo.phone.trim()) {
      err(t("Please enter mobile number"));
      return;
    }

    setIsProcessing(true);

    const created = await handleCreatePOSOrder();
    if (!created) {
      setIsProcessing(false);
      return;
    }

    if (paymentMethod === "send_link") {
      success(`${t("Payment link sent to")} ${customerInfo.phone}`);
      setShowPaymentModal(false);
      setIsProcessing(false);
      clearCart();
      setAmountReceived("");
      setRemarks("");
      return;
    }

    setTimeout(() => {
      const receipt = {
        receiptNumber: `RCP-${Date.now()}`,
        date: new Date().toLocaleString(),
        customer: Object.values(customerInfo).some((value) => value.trim())
          ? customerInfo
          : null,
        items: cart,
        subtotal: getSubtotal(),
        tax: getTax(),
        taxRate: taxType === "percentage" ? tax : null,
        taxType,
        discount: getDiscount(),
        discountValue: discount,
        discountType,
        total: getTotal(),
        paymentMethod,
        amountReceived:
          paymentMethod === "cash" ? parseFloat(amountReceived) : getTotal(),
        change: paymentMethod === "cash" ? getChange() : 0,
      };

      setLastReceipt(receipt);
      setShowPaymentModal(false);
      setShowReceiptModal(true);
      setIsProcessing(false);
      clearCart();
      setAmountReceived("");
      setRemarks("");
    }, 1500);
  };

  const printReceipt = () => {
    window.print();
  };

  const selectedCategory = categories?.find((c) => c.id === selectedCategoryId);

  if (initialLoading) {
    return <LoadingComponent title={t("Point of Sales")} />;
  }

  // Use API-provided total for pagination
  const totalPages = Math.ceil(totalProducts / itemsPerPage);

  return (
    <div className="min-h-screen bg-[#f2e6db]">
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media print {
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area { position: absolute; left: 0; top: 0; }
        }
      `}</style>

      <div className="flex flex-col lg:flex-row h-screen overflow-hidden">
        {/* Left Panel - Products */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-white shadow-lg p-3 sm:p-4 lg:p-6">
            <div className="flex items-center justify-between gap-3 mb-3 sm:mb-4">
              {/* Desktop Header with History Button */}
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="bg-black rounded-full p-2 sm:p-3">
                  <PointOfSale className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h1 className="text-lg sm:text-xl lg:text-3xl font-bold text-gray-800">
                    {t("POS System")}
                  </h1>
                  <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">
                    {t("Quick checkout")}
                  </p>
                </div>

                {/* Desktop History Button */}
                <div className="flex items-center gap-2">
                  <Tooltip
                    title={
                      canViewHistory
                        ? t("View History")
                        : t("You don't have permission to view history")
                    }
                    placement="top"
                  >
                    <button
                      disabled={!canViewHistory}
                      onClick={() => navigate("/admin/pos/history")}
                      className="hidden lg:flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      <History className="w-5 h-5" />
                      <span className="font-medium">{t("History")}</span>
                    </button>
                  </Tooltip>
                </div>
              </div>

              {/* Mobile Cart & History Buttons */}
              <div className="flex items-center gap-2">
                <Tooltip
                  title={
                    canViewHistory
                      ? t("View History")
                      : t("You don't have permission to view history")
                  }
                  placement="top"
                >
                  <button
                    disabled={!canViewHistory}
                    onClick={() => navigate("/admin/pos/history")}
                    className="lg:hidden bg-black text-white p-2 sm:p-3 rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    <History className="w-5 h-5 sm:w-6 sm:h-6" />
                  </button>
                </Tooltip>

                <button
                  onClick={() => setShowCartMobile(true)}
                  className="lg:hidden relative bg-black text-white p-2 sm:p-3 rounded-lg hover:bg-gray-700"
                >
                  <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6" />
                  {cart?.length > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center font-bold">
                      {cart.length}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 sm:pl-11 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none transition-all"
                placeholder={t("Search products...")}
              />
            </div>

            {/* Category Filter Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                className="w-full flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 bg-white border-2 border-gray-200 rounded-lg hover:border-gray-300 transition-all text-sm sm:text-base"
              >
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                  <span className="font-medium text-gray-700">
                    {selectedCategory
                      ? selectedCategory.name.en
                      : t("All Categories")}
                  </span>
                </div>
                <ChevronDown
                  className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-600 transition-transform ${
                    showCategoryDropdown ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showCategoryDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-gray-200 rounded-lg shadow-xl z-30 max-h-64 overflow-y-auto">
                  <button
                    onClick={() => {
                      setSelectedCategoryId("");
                      setShowCategoryDropdown(false);
                    }}
                    className={`w-full text-left px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base hover:bg-gray-50 transition-colors ${
                      !selectedCategoryId ? "bg-gray-100 font-semibold" : ""
                    }`}
                  >
                    {t("All Categories")}
                  </button>
                  {(categories || []).map((category) => (
                    <button
                      key={category.id}
                      onClick={() => {
                        if (category.id) {
                          setSelectedCategoryId(category.id);
                          setShowCategoryDropdown(false);
                        }
                      }}
                      className={`w-full text-left px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base hover:bg-gray-50 transition-colors border-t border-gray-100 ${
                        selectedCategoryId === category.id
                          ? "bg-gray-100 font-semibold"
                          : ""
                      }`}
                    >
                      {category.name.en}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6">
            {error ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                  <p className="text-red-600 text-lg font-semibold mb-2">
                    {t("Error Loading Products")}
                  </p>
                  <p className="text-gray-600">{error}</p>
                </div>
              </div>
            ) : (
              <div
                className={
                  "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 lg:gap-4" +
                  (loading
                    ? " opacity-50 pointer-events-none transition-opacity"
                    : "")
                }
              >
                {filtered.map((product) => (
                  <div
                    key={product.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleProductClick(product);
                    }}
                    className="bg-white rounded-lg sm:rounded-xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1 p-2 sm:p-3 lg:p-4"
                    style={{ animation: "fadeIn 0.3s ease-out" }}
                  >
                    <div className="bg-gray-100 rounded-lg h-20 sm:h-24 lg:h-32 flex items-center justify-center mb-2 sm:mb-3 overflow-hidden">
                      {product.images &&
                      product.images?.length > 0 &&
                      product.images[0] ? (
                        <img
                          src={product.images[0]}
                          alt={product.name.en}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Package2 className="w-8 h-8 sm:w-10 sm:h-10 lg:w-16 lg:h-16 text-gray-400" />
                      )}
                    </div>
                    <h3 className="font-bold text-gray-800 text-xs sm:text-sm lg:text-base mb-1 truncate">
                      {product.name.en}
                    </h3>
                    <p className="text-xs text-gray-500 mb-1 sm:mb-2">
                      {product.sku}
                    </p>
                    {product.variations && product.variations.length && (
                      <p className="text-xs text-blue-600 mb-1">
                        {product.variations.length} {t("variations")}
                      </p>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-sm sm:text-base lg:text-xl font-bold text-black">
                        <Price amount={Number(product.price)} />
                      </span>
                      <span
                        className={`text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full ${
                          product.stockStatus === "in_stock"
                            ? "bg-green-100 text-green-800"
                            : product.stockStatus === "low_stock"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                        }`}
                      >
                        {product.stock}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!loading && !error && filtered?.length === 0 && (
              <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-16 text-center">
                <Package2 className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-base sm:text-lg">
                  {t("No product found")}
                </p>
              </div>
            )}
          </div>

          {/* Pagination Indicators */}
          {apiTotalItems > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={apiTotalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => {
                setItemsPerPage(size);
                setCurrentPage(1);
              }}
              pageSizeOptions={[5, 10, 25, 50, 100]}
              showPageSizeSelector={true}
              isLoading={loading}
              compact={true}
            />
          )}
        </div>

        {/* Desktop Cart - Right Panel */}
        <div
          className="hidden lg:flex w-[420px] bg-white shadow-2xl flex-col"
          style={{ animation: "slideIn 0.3s ease-out" }}
        >
          <CartContent
            cart={cart}
            customerInfo={customerInfo}
            setCustomerInfo={setCustomerInfo}
            updateQuantity={updateQuantity}
            removeFromCart={removeFromCart}
            clearCart={clearCart}
            handleCheckout={handleCheckout}
            getSubtotal={getSubtotal}
            getTax={getTax}
            getDiscount={getDiscount}
            getTotal={getTotal}
            tax={tax}
            setTax={(value) => setTax(Math.max(0, value))}
            taxType={taxType}
            setTaxType={setTaxType}
            discount={discount}
            setDiscount={(value) => setDiscount(Math.max(0, value))}
            discountType={discountType}
            setDiscountType={setDiscountType}
          />
        </div>

        {/* Mobile Cart - Bottom Sheet */}
        {showCartMobile && (
          <div
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setShowCartMobile(false)}
          >
            <div
              className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl max-h-[85vh] flex flex-col"
              style={{ animation: "slideUp 0.3s ease-out" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  {t("Cart")} ({cart?.length})
                </h2>
                <button
                  onClick={() => setShowCartMobile(false)}
                  className="p-1"
                >
                  <X className="w-6 h-6 text-gray-600" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto">
                <CartContent
                  cart={cart}
                  customerInfo={customerInfo}
                  setCustomerInfo={setCustomerInfo}
                  updateQuantity={updateQuantity}
                  removeFromCart={removeFromCart}
                  clearCart={clearCart}
                  handleCheckout={handleCheckout}
                  getSubtotal={getSubtotal}
                  getTax={getTax}
                  getDiscount={getDiscount}
                  getTotal={getTotal}
                  tax={tax}
                  setTax={(value) => setTax(Math.max(0, value))}
                  taxType={taxType}
                  setTaxType={setTaxType}
                  discount={discount}
                  setDiscount={(value) => setDiscount(Math.max(0, value))}
                  discountType={discountType}
                  setDiscountType={setDiscountType}
                  isMobile={true}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Variation Selection Modal */}
      {showVariationModal && selectedProduct && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={closeVariationModal}
        >
          <div
            className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-black via-gray-700 to-black px-4 sm:px-6 py-3 sm:py-4 rounded-t-xl sm:rounded-t-2xl flex items-center justify-between sticky top-0">
              <h3 className="text-lg sm:text-xl font-bold text-white">
                {t("Select Variation")}
              </h3>
              <button
                onClick={closeVariationModal}
                className="text-white hover:text-gray-300 transition-colors"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            <div className="p-4 sm:p-6">
              <div className="mb-4">
                <h4 className="text-xl font-bold text-gray-800">
                  {selectedProduct.name.en}
                </h4>
                <p className="text-sm text-gray-600">{selectedProduct.sku}</p>
              </div>

              {variationModalLoading ? (
                <div className="py-8 text-center text-gray-600">
                  {t("Loading options...")}
                </div>
              ) : variationModalError ? (
                <div className="py-6 px-4 rounded-lg bg-red-50 text-red-600 text-sm">
                  {variationModalError}
                </div>
              ) : (
                <>
                  {requiresVariationSelection ? (
                    <div className="space-y-4">
                      {Object.entries(variationOptions).map(
                        ([type, values]) => (
                          <div key={type}>
                            <p className="text-xs font-semibold tracking-wide text-gray-500 mb-2 uppercase">
                              {t("Select")} {type}
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {values.map((value) => (
                                <button
                                  key={`${type}-${value}`}
                                  type="button"
                                  className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-colors ${
                                    selectedVariationAttributes[type] === value
                                      ? "bg-black text-white border-black"
                                      : "bg-white text-gray-700 border-gray-300 hover:border-black"
                                  }`}
                                  onClick={() =>
                                    handleVariationAttributeClick(type, value)
                                  }
                                >
                                  {value}
                                </button>
                              ))}
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-600">
                      {t("No options required. You can add this item to cart.")}
                    </div>
                  )}

                  <div className="mt-6 rounded-lg border border-gray-200 p-4 bg-gray-50">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-xs text-gray-500">{t("SKU")}</p>
                        <p className="font-medium text-gray-800 text-sm">
                          {selectedVariation?.sku || selectedProduct.sku}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">{t("Price")}</p>
                        <p className="font-bold text-black text-lg">
                          <Price
                            amount={Number(
                              selectedVariation?.price || selectedProduct.price,
                            )}
                          />
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {t("Stock")}:{" "}
                          {selectedVariation?.stock ?? selectedProduct.stock}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeVariationModal}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  {t("Cancel")}
                </button>
                <button
                  type="button"
                  onClick={handleAddFromVariationModal}
                  disabled={
                    variationModalLoading ||
                    !!variationModalError ||
                    isVariationModalOutOfStock ||
                    (requiresVariationSelection && !selectedVariation)
                  }
                  className="px-4 py-2 rounded-lg bg-black text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isVariationModalOutOfStock
                    ? t("Out of Stock")
                    : t("Add to Cart")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowPaymentModal(false)}
        >
          <div
            className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-black via-gray-700 to-black px-4 sm:px-6 py-3 sm:py-4 rounded-t-xl sm:rounded-t-2xl flex items-center justify-between sticky top-0">
              <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                {currency.symbol}
                {t("Process Payment")}
              </h3>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="text-white hover:text-gray-300 transition-colors"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                <div className="flex justify-between mb-2">
                  <span className="text-sm sm:text-base text-gray-600">
                    {t("Total Amount")}:
                  </span>
                  <span className="text-xl sm:text-2xl font-bold text-black">
                    <Price amount={Number(getTotal())} />
                  </span>
                </div>
                <div className="space-y-1 text-xs sm:text-sm border-t border-gray-200 pt-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t("Subtotal")}:</span>
                    <span className="font-semibold">
                      <Price amount={Number(getSubtotal())} />
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      {t("Tax")}
                      {taxType === "percentage" ? ` (${tax}%)` : ""}:
                    </span>
                    <span className="font-semibold">
                      <Price amount={Number(getTax())} />
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      {t("Discount")}
                      {discountType === "percentage" ? ` (${discount}%)` : ""}:
                    </span>
                    <span className="font-semibold text-red-600">
                      -<Price amount={Number(getDiscount())} />
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  {t("Payment Method")}
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-3">
                  <button
                    onClick={() => setPaymentMethod("cash")}
                    className={`flex flex-col items-center gap-2 p-3 sm:p-4 rounded-lg border-2 transition-all ${
                      paymentMethod === "cash"
                        ? "border-black bg-gray-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <Banknote className="w-6 h-6 sm:w-8 sm:h-8" />
                    <span className="font-semibold text-sm sm:text-base">
                      {t("Cash")}
                    </span>
                  </button>
                  <button
                    onClick={() => setPaymentMethod("card")}
                    className={`flex flex-col items-center gap-2 p-3 sm:p-4 rounded-lg border-2 transition-all ${
                      paymentMethod === "card"
                        ? "border-black bg-gray-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <CreditCard className="w-6 h-6 sm:w-8 sm:h-8" />
                    <span className="font-semibold text-sm sm:text-base">
                      {t("Card")}
                    </span>
                  </button>
                  <button
                    onClick={() => setPaymentMethod("credit")}
                    className={`flex flex-col items-center gap-2 p-3 sm:p-4 rounded-lg border-2 transition-all ${
                      paymentMethod === "credit"
                        ? "border-black bg-gray-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <CreditCard className="w-6 h-6 sm:w-8 sm:h-8" />
                    <span className="font-semibold text-sm sm:text-base">
                      {t("Credit")}
                    </span>
                  </button>
                  <button
                    onClick={() => setPaymentMethod("maagoud_gift")}
                    className={`flex flex-col items-center gap-2 p-3 sm:p-4 rounded-lg border-2 transition-all ${
                      paymentMethod === "maagoud_gift"
                        ? "border-black bg-gray-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <Gift className="w-6 h-6 sm:w-8 sm:h-8" />
                    <span className="font-semibold text-sm sm:text-base">
                      {t("Maagoud Gift")}
                    </span>
                  </button>
                  {/* <button
                    onClick={() => setPaymentMethod("send_link")}
                    className={`flex flex-col items-center gap-2 p-3 sm:p-4 rounded-lg border-2 transition-all ${
                      paymentMethod === "send_link"
                        ? "border-black bg-gray-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <Send className="w-6 h-6 sm:w-8 sm:h-8" />
                    <span className="font-semibold text-sm sm:text-base">
                      {t("Send Link")}
                    </span>
                  </button> */}
                </div>
              </div>

              {paymentMethod === "cash" && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("Amount Received")}
                  </label>
                  <input
                    type="number"
                    value={amountReceived}
                    onChange={(e) => setAmountReceived(e.target.value)}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none text-base sm:text-lg"
                    placeholder="0.00"
                    step="0.01"
                  />
                  {amountReceived &&
                    parseFloat(amountReceived) >= getTotal() && (
                      <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="flex justify-between">
                          <span className="text-green-700 font-semibold text-sm sm:text-base">
                            {t("Change")}:
                          </span>
                          <span className="text-green-700 font-bold text-base sm:text-lg">
                            <Price amount={Number(getChange())} />
                          </span>
                        </div>
                      </div>
                    )}
                </div>
              )}

              {paymentMethod === "send_link" && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("Mobile Number")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={customerInfo.phone}
                    onChange={(e) =>
                      setCustomerInfo({
                        ...customerInfo,
                        phone: e.target.value,
                      })
                    }
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none text-base sm:text-lg"
                    placeholder={t("Enter mobile number")}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Internal remarks (staff only)")}{" "}
                  <span className="font-normal text-gray-500">
                    ({t("Optional")})
                  </span>
                </label>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  rows={2}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none text-sm sm:text-base resize-none"
                  placeholder={t("Not shown to the customer or on the receipt")}
                />
              </div>

              <button
                onClick={processPayment}
                disabled={
                  isProcessing ||
                  (paymentMethod === "cash" &&
                    parseFloat(amountReceived) < getTotal())
                }
                className="w-full py-2.5 sm:py-3 bg-black text-white rounded-lg hover:bg-gray-700 transition-all transform hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed font-semibold flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                {isProcessing ? (
                  <>
                    <div
                      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                      style={{ animation: "spin 1s linear infinite" }}
                    ></div>
                    {t("Processing...")}
                  </>
                ) : (
                  <>
                    {paymentMethod === "send_link" ? (
                      <>
                        <Send className="w-5 h-5" />
                        {t("Send Payment Link")}
                      </>
                    ) : (
                      <>
                        <Receipt className="w-5 h-5" />
                        {t("Complete Payment")}
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      {showReceiptModal && lastReceipt && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowReceiptModal(false)}
        >
          <div
            className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-4 sm:px-6 py-3 sm:py-4 rounded-t-xl sm:rounded-t-2xl flex items-center justify-between sticky top-0">
              <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                <Receipt className="w-5 h-5 sm:w-6 sm:h-6" />
                {t("Payment Successful")}
              </h3>
              <button
                onClick={() => setShowReceiptModal(false)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            <div className="p-4 sm:p-6 print-area">
              <div className="text-center mb-4 sm:mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1">
                  {t("RECEIPT")}
                </h2>
                <p className="text-xs sm:text-sm text-gray-600">
                  {lastReceipt.receiptNumber}
                </p>
                <p className="text-xs text-gray-500">{lastReceipt.date}</p>
              </div>

              {lastReceipt.customer && (
                <div className="mb-4 pb-4 border-b border-gray-200">
                  <p className="text-sm font-semibold text-gray-700">
                    {t("Customer")}:
                  </p>
                  {lastReceipt.customer.name && (
                    <p className="text-sm text-gray-600">
                      {lastReceipt.customer.name}
                    </p>
                  )}
                  {lastReceipt.customer.phone && (
                    <p className="text-sm text-gray-600">
                      {lastReceipt.customer.phone}
                    </p>
                  )}
                  {(lastReceipt.customer.city ||
                    lastReceipt.customer.zone ||
                    lastReceipt.customer.street ||
                    lastReceipt.customer.buildingNumber ||
                    lastReceipt.customer.unitNumber) && (
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">{t("Address")}: </span>
                      {[
                        lastReceipt.customer.city &&
                          `${t("City")}: ${lastReceipt.customer.city}`,
                        lastReceipt.customer.zone &&
                          `${t("Zone")}: ${lastReceipt.customer.zone}`,
                        lastReceipt.customer.street &&
                          `${t("Street")}: ${lastReceipt.customer.street}`,
                        lastReceipt.customer.buildingNumber &&
                          `${t("Building Number")}: ${lastReceipt.customer.buildingNumber}`,
                        lastReceipt.customer.unitNumber &&
                          `${t("Unit Number")}: ${lastReceipt.customer.unitNumber}`,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-2 mb-4">
                {lastReceipt.items.map((item: CartItem, index: number) => (
                  <div
                    key={index}
                    className="flex justify-between text-xs sm:text-sm"
                  >
                    <div className="flex-1">
                      <span className="text-gray-700">
                        {item.name} x{item.quantity}
                      </span>
                      {item.variationAttributes && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {Object.entries(item.variationAttributes).map(
                            ([key, value]) => (
                              <span
                                key={key}
                                className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded"
                              >
                                {key}: {value}
                              </span>
                            ),
                          )}
                        </div>
                      )}
                    </div>
                    <span className="font-semibold text-gray-800 ml-2">
                      <Price amount={Number(item.total)} />
                    </span>
                  </div>
                ))}
              </div>

              <div className="border-t-2 border-gray-200 pt-3 space-y-2">
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-gray-600">{t("Subtotal")}:</span>
                  <span className="font-semibold">
                    <Price amount={Number(lastReceipt.subtotal)} />
                  </span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-gray-600">
                    {t("Tax")}
                    {lastReceipt.taxType === "percentage" &&
                    lastReceipt.taxRate !== null
                      ? ` (${Number(lastReceipt.taxRate)}%)`
                      : ""}
                    :
                  </span>
                  <span className="font-semibold">
                    <Price amount={Number(lastReceipt.tax)} />
                  </span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-gray-600">
                    {t("Discount")}
                    {lastReceipt.discountType === "percentage"
                      ? ` (${Number(lastReceipt.discountValue)}%)`
                      : ""}
                    :
                  </span>
                  <span className="font-semibold text-red-600">
                    -<Price amount={Number(lastReceipt.discount)} />
                  </span>
                </div>
                <div className="flex justify-between text-base sm:text-lg font-bold border-t-2 border-gray-200 pt-2">
                  <span>{t("Total")}:</span>
                  <span>
                    <Price amount={Number(lastReceipt.total)} />
                  </span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-gray-600">{t("Payment")}:</span>
                  <span className="font-semibold capitalize">
                    {t(
                      lastReceipt.paymentMethod === "maagoud_gift"
                        ? "Maagoud Gift"
                        : lastReceipt.paymentMethod === "send_link"
                          ? "Send Link"
                          : lastReceipt.paymentMethod === "credit"
                            ? "Credit"
                            : lastReceipt.paymentMethod === "card"
                              ? "Card"
                              : "Cash",
                    )}
                  </span>
                </div>
                {lastReceipt.paymentMethod === "cash" && (
                  <>
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="text-gray-600">{t("Received")}:</span>
                      <span className="font-semibold">
                        <Price amount={Number(lastReceipt.amountReceived)} />
                      </span>
                    </div>
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="text-gray-600">{t("Change")}:</span>
                      <span className="font-semibold text-green-600">
                        <Price amount={Number(lastReceipt.change)} />
                      </span>
                    </div>
                  </>
                )}
              </div>

              <div className="text-center mt-4 sm:mt-6 pt-4 border-t border-gray-600">
                <p className="text-xs text-gray-500">
                  {t("Thank you for your business!")}
                </p>
              </div>
            </div>

            <div className="p-4 sm:p-6 pt-0 flex gap-2 sm:gap-3">
              <button
                onClick={printReceipt}
                className="flex-1 py-2.5 sm:py-3 bg-black text-white rounded-lg hover:bg-gray-700 transition-all font-semibold flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                <Receipt className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">{t("Print Receipt")}</span>
                <span className="sm:hidden">{t("Print")}</span>
              </button>
              <button
                onClick={() => setShowReceiptModal(false)}
                className="flex-1 py-2.5 sm:py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-semibold text-sm sm:text-base"
              >
                {t("Close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default POSPage;
